import ReactDOM from 'react-dom'
import { take, takeLatest, call, put, fork, select } from 'redux-saga/effects'

import * as auth from 'auth'
import { paymentSuccess, paymentCancelled, paymentError } from 'basket/actions'
import { apiLanguageCodeSelector } from 'i18n/selectors'
import { LOCATION_CHANGE } from 'browser-location'

import * as creditCardConstants from 'credit-cards/constants'
import * as creditCardActions from 'credit-cards/actions'
import * as creditCardApi from 'credit-cards/grsApi'

import * as constants from 'credit-cards/ingenico/constants'
import * as actions from 'credit-cards/ingenico/actions'
import * as api from 'credit-cards/ingenico/api'

import Secure3dFormModal from './secure3d'

const generateParameters = (orderId, creditCardBrand, pspId, apiLanguageCode) => ({
    'ACCOUNT.PSPID': pspId,
    'ALIAS.ORDERID': orderId,
    'CARD.BRAND': creditCardBrand,
    'CARD.PAYMENTMETHOD': 'CreditCard',
    'LAYOUT.TEMPLATENAME': 'master.html',
    'LAYOUT.LANGUAGE': apiLanguageCode,
    'PARAMETERS.ACCEPTURL': location.origin + constants.CALLBACK_URL,
    'PARAMETERS.EXCEPTIONURL': location.origin + constants.CALLBACK_URL,
})

function* ingenicoIframeSaga() {
    while (true) {
        const { name, creditCardBrand } = yield take(constants.CREDIT_CARD_BRAND_SELECTED)

        if (!document.querySelector(`iframe[name=${name}]`)) continue

        const orderId = yield call(api.generateOrder)

        const apiLanguageCode = yield select(apiLanguageCodeSelector)
        yield call(api.setIngenicoPaymentResponse, orderId, apiLanguageCode)

        const pspId = yield call(api.getProcessorManager)
        const parameters = generateParameters(orderId, creditCardBrand, pspId, apiLanguageCode)
        const signature = yield call(api.getIngenicoSHA, parameters)

        const form = document.createElement('form')
        form.action = INGENICO_HOSTED_PAGE
        form.target = name
        form.method = 'POST'

        document.body.appendChild(form)

        const formFields = { ...parameters, 'ShaSignature.ShaSign': signature }

        Object.keys(formFields).forEach(key => {
            const input = document.createElement('input')
            input.type = 'hidden'
            input.name = key
            input.value = formFields[key]

            form.appendChild(input)
        })

        form.submit()
        document.body.removeChild(form)
    }
}

const secure3dVerification = body => {
    const formContainer = document.createElement('div')
    window.document.body.appendChild(formContainer)
    ReactDOM.render(<Secure3dFormModal body={body} />, formContainer)

    return formContainer
}

function* ingenicoTokenizationSaga() {
    while (true) {
        const { details, errors } = yield take(creditCardConstants.PSP_VERIFICATION_RESPONSE)
        if (errors.length > 0) {
            yield put(actions.ingenicoVerificationFailed(errors))
            continue
        }

        const {
            // 'Alias.OrderId': orderId,
            'Alias.AliasId': aliasId,
            'Card.Brand': brand,
            'Card.CardNumber': cardNumber,
            'Card.CardHolderName': name,
            'Card.ExpiryDate': expiryDate,
        } = details

        details['ACCEPTURL'] = location.origin + constants.CALLBACK_URL
        details['DECLINEURL'] = location.origin + constants.CALLBACK_URL
        details['EXCEPTIONURL'] = location.origin + constants.CALLBACK_URL

        try {
            const { token } = yield select(auth.selectors.userSelector)
            const response = yield call(api.acceptIngenicoPayment, details)
            const body = yield response.text()

            if (body !== 'PROCESSING') {
                yield put(actions.init3DSecureFlow())
                yield call(secure3dVerification, body)

                const { errors } = yield take(creditCardConstants.SECURE3D_RESPONSE)
                if (errors.length > 0) {
                    yield put(actions.ingenicoVerificationFailed(errors))
                    continue
                }
            }

            const cardData = {
                ccToken: aliasId,
                ExpDate: expiryDate,
                CardType: brand,
                CardLast4: cardNumber,
                Name: name,
                CardProcessor: 'INGENICO',
            }

            yield call(creditCardApi.saveCardData, token, cardData)
            yield put(creditCardActions.addPaymentMethodSuccess())
        } catch (error) {}
    }
}

function* ingenicoPaymentSaga() {
    while (true) {
        const { card, paymentTotal, locationId, orderId } = yield take(creditCardConstants.INIT_PAYMENT)
        const { token } = yield select(auth.selectors.userSelector)

        try {
            const apiLanguageCode = yield select(apiLanguageCodeSelector)

            const { html3DS: secure3dForm } = yield call(
                api.authoriseTransaction,
                orderId,
                token,
                card,
                paymentTotal,
                locationId,
                apiLanguageCode
            )

            if (secure3dForm) {
                const formContainer = yield call(secure3dVerification, secure3dForm)

                const { type, errors } = yield take([
                    creditCardConstants.SECURE3D_RESPONSE,
                    creditCardConstants.SECURE3D_CANCELLED,
                ])

                ReactDOM.unmountComponentAtNode(formContainer)

                if (type === creditCardConstants.SECURE3D_CANCELLED) {
                    yield put(paymentCancelled())
                    continue
                }

                if (errors.length > 0) {
                    yield put(actions.ingenicoVerificationFailed(errors))
                    yield put(paymentError(`3DSecure verification failed: ${errors[0].name} ${errors[0].code}`))
                    continue
                }
            }

            yield put(paymentSuccess(orderId))
        } catch (exception) {
            yield put(paymentError(exception.message))
        }
    }
}

function* locationChangeSaga() {
    yield takeLatest(constants.INGENICO_VERIFICATION_FAILED, function*() {
        yield take(LOCATION_CHANGE)
        yield put(actions.clearErrors())
    })
}

export function* rootSaga() {
    yield fork(ingenicoIframeSaga)
    yield fork(ingenicoTokenizationSaga)
    yield fork(ingenicoPaymentSaga)
    yield fork(locationChangeSaga)
}
